import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAppSelector } from '@/store/hooks';
import { ProtectedRoute } from '@/components/protected-route';
import { routeAccess } from '@/lib/navigation';

import {
  AddPropertyPage,
  TemplatesPage,
  HealthPage,
  HomePage,
  LoginPage,
  RegisterPage,
  PasswordResetPage,
  PropertyListPage,
  SettingsPage,
  SettingsProfilePage,
  AddTemplatePage,
  UnauthorizedPage,
  InvitePage,
  CreateProfilePage,
} from '@/pages';

import MobileRemoteConfigPage from '@/pages/private/admin/mobile-config';
import SchemasPage from './pages/private/property-schemas/schemas';
import AddSchemaPage from './pages/private/property-schemas/schema-add';
import SubscriptionsPage from '@/pages/private/admin/subscriptions';
import PropertyEditorPage from './pages/private/properties/components/PropertyEditorPage';
import OnboardingWizardPage from './pages/private/onboarding/OnboardingWizardPage';
import VerifyPhoneStep from './pages/private/onboarding/verify-phone';
import OrganizationStep from './pages/private/onboarding/organization-step';
import AdminUsersPage from './pages/private/admin/users/users-list';
import { selectActiveOrganizationId, selectCurrentUserProfile } from './store/selectors/authorizationSelectors';
import AdminOrganizationsPage from '@/pages/private/organizations/organizations';
import AdminOrganizationDetailsPage from '@/pages/private/organizations/organization-details';
import AcceptInvitationPage from '@/pages/private/invitations/AcceptInvitationPage';
import SupportTicketsPage from '@/pages/private/admin/support';
import SupportTicketThreadPage from '@/pages/private/admin/support-ticket';
import BusinessUsersPoolPage from '@/pages/private/admin/business-users-pool';
import HomeownerUsersPoolPage from '@/pages/private/admin/homeowner-users-pool';
import HomeownersPage from '@/pages/private/admin/homeowners';
import HomeownerDetailsPage from '@/pages/private/admin/homeowner-details';
import RecoveryCasesPage from '@/pages/private/admin/recovery-cases';
import RecoveryCaseDetailPage from '@/pages/private/admin/recovery-case-detail';

function App() {
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const profile = useAppSelector(selectCurrentUserProfile);
  const allowCreateOrg = !!profile?.allowCreateOrg;
  const organizationID = useAppSelector(selectActiveOrganizationId);

  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />} />
        <Route path="/register" element={isAuthenticated ? <Navigate to="/" replace /> : <RegisterPage />} />
        <Route
          path="/forgot-password"
          element={isAuthenticated ? <Navigate to="/" replace /> : <PasswordResetPage />}
        />
        <Route path="/invite" element={<InvitePage />} />

        <Route element={<ProtectedRoute noLayout allowDuringOnboarding />}>
          <Route path="/invite/accept" element={<AcceptInvitationPage />} />
        </Route>

        {/* Protected routes - requires authentication */}
        <Route element={<ProtectedRoute />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/properties" element={<PropertyListPage />} />
          <Route path="/properties/add" element={<AddPropertyPage />} />

          {/* ✅ Property editor */}
          <Route path="/properties/new" element={<PropertyEditorPage />} />
          <Route path="/properties/:propertyId/edit" element={<PropertyEditorPage />} />

          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/settings/profile" element={<SettingsProfilePage />} />
          <Route path="/health" element={<HealthPage />} />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />

          {/* ✅ Onboarding (Wizard + deep-linkable steps) */}
          <Route path="/onboarding">
            {/* Wizard */}
            <Route index element={<OnboardingWizardPage />} />

            {/* Steps (deep links from settings/admin banners) */}
            <Route path="profile" element={<CreateProfilePage />} />
            <Route
              path="phone"
              element={
                <VerifyPhoneStep
                  onSuccess={() => {
                    /* later: dispatch setPhoneVerified or refetch profile */
                  }}
                />
              }
            />
            <Route
              path="organization"
              element={
                <OrganizationStep allowCreateOrg={allowCreateOrg} organizationID={organizationID ?? undefined} />
              }
            />
          </Route>

          {/* Backwards compat (old route) */}
          <Route path="/create-profile" element={<Navigate to="/onboarding/profile" replace />} />
        </Route>

        {/* Organization-scoped routes */}
        <Route element={<ProtectedRoute {...routeAccess.templates} />}>
          <Route path="/templates">
            <Route index element={<TemplatesPage />} />
            <Route path="add" element={<AddTemplatePage />} />
            <Route path=":templateId/edit" element={<AddTemplatePage />} />
          </Route>
        </Route>

        <Route element={<ProtectedRoute {...routeAccess.myOrganization} />}>
          <Route path="/admin/organizations/:organizationId" element={<AdminOrganizationDetailsPage />} />
        </Route>

        {/* Super Admin only routes */}
        <Route element={<ProtectedRoute {...routeAccess.adminOnly} />}>
          <Route path="/admin/organizations" element={<AdminOrganizationsPage />} />
          <Route path="/admin/mobile-config" element={<MobileRemoteConfigPage />} />

          <Route path="/admin/users" element={<AdminUsersPage />} />
          <Route path="/admin/homeowners" element={<HomeownersPage />} />
          <Route path="/admin/homeowners/:homeownerId" element={<HomeownerDetailsPage />} />
          <Route path="/admin/business-users-pool" element={<BusinessUsersPoolPage />} />
          <Route path="/admin/homeowner-users-pool" element={<HomeownerUsersPoolPage />} />
          <Route path="/admin/support" element={<SupportTicketsPage />} />
          <Route path="/admin/support/:ticketId" element={<SupportTicketThreadPage />} />
          <Route path="/admin/recovery-cases" element={<RecoveryCasesPage />} />
          <Route path="/admin/recovery-cases/:caseId" element={<RecoveryCaseDetailPage />} />

          <Route path="/admin/schemas">
            <Route index element={<SchemasPage />} />
            <Route path="add" element={<AddSchemaPage />} />
            <Route path=":schemaId/edit" element={<AddSchemaPage />} />
            <Route path=":schemaId/view/:version" element={<AddSchemaPage />} />
          </Route>

          <Route path="/admin/subscriptions" element={<SubscriptionsPage />} />
        </Route>

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
